import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import api from '../../services/api';

export default function ReplaceCardDialog({ targetId, onClose, onComplete }) {
  const [feeds, setFeeds] = useState([]);
  const [externalId, setExternalId] = useState('');
  const [preview, setPreview] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!targetId) return;
    let live = true;
    setPreview(null); setExternalId(''); setDuplicates([]); setError(''); setBusy(true);
    api.get('/akahu/feeds').then(res => { if (live) setFeeds(res.data.items || []); })
      .catch(err => { if (live) setError(err.response?.data?.error || 'Could not load bank accounts.'); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [targetId]);

  const review = async () => {
    setBusy(true); setError('');
    try {
      const feed = feeds.find(f => f.externalId === externalId);
      if (!feed?.lastSyncedAt || !feed.localAccountId) {
        await api.put(`/akahu/feeds/${externalId}`, { included: true, localAccountId: feed?.localAccountId || null });
        const sync = await api.post('/akahu/sync');
        if (sync.status === 202 || sync.data?.inProgress) {
          setError('Replacement history is loading into a separate account. Wait for sync to finish, then preview again.');
          return;
        }
        const refreshed = await api.get('/akahu/feeds');
        setFeeds(refreshed.data.items || []);
      }
      const res = await api.post('/akahu/replacement/preview', { targetId, externalId });
      setPreview(res.data); setDuplicates([]);
    } catch (err) { setError(err.response?.data?.error || 'Could not prepare the combination.'); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true); setError('');
    try {
      await api.post('/akahu/replacement/confirm', { targetId, externalId, token: preview.token, duplicateIds: duplicates });
      await onComplete();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not combine accounts.');
      if (err.response?.status === 409) setPreview(null);
    } finally { setBusy(false); }
  };

  return <Dialog open={Boolean(targetId)} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle>Replace a card / combine bank history</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2">Choose the replacement bank feed. Its history will join this Budget Clues account, and it will supply the current balance. The old feed can stay linked for late settlements. If the replacement has not been imported yet, we first load its available history into a separate account for review. Cancelling leaves that account separate.</Typography>
        <TextField select fullWidth label="Replacement bank account" value={externalId} disabled={busy || Boolean(preview)} onChange={e => setExternalId(e.target.value)}>
          {feeds.filter(f => f.bank && f.localAccountId !== targetId).map(f => <MenuItem key={f.externalId} value={f.externalId}>
            {f.bank.connection?.name} · {f.name} {f.bank.formatted_account ? ` · …${f.bank.formatted_account.replace(/\s/g, '').slice(-4)}` : ''}
          </MenuItem>)}
        </TextField>
        {!busy && !feeds.some(f => f.bank && f.localAccountId !== targetId) && <Alert severity="info">No replacement feed is available. Use Settings → Manage bank accounts to share it first.</Alert>}
        {preview && <>
          <Alert severity="info">Keep “{preview.targetName}” and move {preview.transactionsToMove} transactions from “{preview.sourceName}”. Existing categorisation stays with each transaction. Any separate source account will be archived. Balances are never added together.</Alert>
          <Typography variant="subtitle2">Review possible duplicates ({preview.candidates.length})</Typography>
          <Typography variant="body2">Matching date, amount and description can also mean separate purchases. Nothing is selected automatically. Tick only replacement transactions you want removed; the matching existing transactions will remain. You can undo the latest combination in Settings.</Typography>
          {preview.candidates.map(row => <Box key={row.sourceId} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
            <FormControlLabel control={<Checkbox checked={duplicates.includes(row.sourceId)} disabled={busy} onChange={e => setDuplicates(ids => e.target.checked ? [...ids, row.sourceId] : ids.filter(id => id !== row.sourceId))} />}
              label={<Typography variant="body2">Remove replacement copy: {String(row.date).slice(0, 10)} · {row.amount} · {row.description}</Typography>} />
            <Typography variant="caption" display="block">Replacement category: {row.category || 'Uncategorised'} {row.notes ? ` · ${row.notes}` : ''}</Typography>
            {row.matches.map((t, index) => <Typography key={index} variant="caption" display="block">Keep existing: {t.description} · {t.amount} · {t.category || 'Uncategorised'} {t.notes ? ` · ${t.notes}` : ''}</Typography>)}
          </Box>)}
          <Typography variant="body2">Result: {preview.transactionsToMove - duplicates.length} transactions moved; {duplicates.length} reviewed copies removed.</Typography>
        </>}
      </Stack>
    </DialogContent>
    <DialogActions>
      {busy && <CircularProgress size={20} />}
      <Button onClick={onClose} disabled={busy}>Cancel</Button>
      {preview ? <>
        <Button onClick={() => setPreview(null)} disabled={busy}>Back</Button>
        <Button variant="contained" onClick={confirm} disabled={busy}>Confirm combination</Button>
      </> : <Button variant="contained" onClick={review} disabled={busy || !externalId}>Load history & preview</Button>}
    </DialogActions>
  </Dialog>;
}
